import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n';
import { RoomRate, RoomType, Currency, AmountType, CreateRoomRateRequest } from '../types/rooms';
import { roomService } from '../services/roomService';
import { useHotelPermissions } from '../hooks/useHotelPermissions';

const CURRENCIES: Currency[] = ['USD', 'CDF', 'EUR'];
const AMOUNT_TYPES: { value: AmountType; label: string }[] = [
  { value: 'HT', label: 'HT (Hors Taxes)' },
  { value: 'TTC', label: 'TTC (Toutes Taxes Comprises)' },
];

interface FormState {
  room_type_id: string;
  price_per_night: string;
  currency: Currency;
  amount_type: AmountType;
  start_date: string;
  end_date: string;
}

const emptyForm: FormState = {
  room_type_id: '',
  price_per_night: '',
  currency: 'USD',
  amount_type: 'HT',
  start_date: '',
  end_date: '',
};

export default function RatesPage() {
  const { t } = useTranslation();
  const { isSuperAdmin } = useHotelPermissions();
  const [rates, setRates] = useState<RoomRate[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ratesData, typesData] = await Promise.all([
        roomService.listRoomRates(),
        roomService.listRoomTypes(),
      ]);
      setRates(ratesData);
      setRoomTypes(typesData);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currencySymbol = (c: Currency): string => {
    const map: Record<Currency, string> = { USD: '$', CDF: 'FC', EUR: '€' };
    return map[c] || c;
  };

  const isExpired = (endDate: string): boolean => new Date(endDate) < new Date();

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, room_type_id: roomTypes[0]?.id || '' });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (rate: RoomRate) => {
    if (!isSuperAdmin) return;
    setEditingId(rate.id);
    setForm({
      room_type_id: rate.room_type_id,
      price_per_night: String(rate.price_per_night),
      currency: rate.currency,
      amount_type: rate.amount_type || 'HT',
      start_date: rate.start_date,
      end_date: rate.end_date,
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId && !isSuperAdmin) {
      setFormError(t('rates.readOnly'));
      return;
    }
    const price = parseFloat(form.price_per_night);
    if (!form.room_type_id) { setFormError(t('rates.roomType') + ' required'); return; }
    if (isNaN(price) || price <= 0) { setFormError(t('rates.pricePerNight') + ' > 0'); return; }
    if (!form.start_date || !form.end_date) { setFormError(t('rates.startDate') + ' & ' + t('rates.endDate') + ' required'); return; }
    if (form.start_date >= form.end_date) { setFormError(t('rates.startDate') + ' < ' + t('rates.endDate')); return; }

    const payload: CreateRoomRateRequest = {
      room_type_id: form.room_type_id,
      price_per_night: price,
      currency: form.currency,
      amount_type: form.amount_type,
      start_date: form.start_date,
      end_date: form.end_date,
    };

    try {
      setSaving(true);
      setFormError(null);
      if (editingId) {
        await roomService.updateRoomRate(editingId, payload);
      } else {
        await roomService.createRoomRate(payload);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      const msg = typeof e === 'string' ? e : e?.message || e?.error || JSON.stringify(e);
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!isSuperAdmin) {
      setError(t('rates.readOnly'));
      return;
    }
    try {
      await roomService.deleteRoomRate(id);
      setDeleteConfirm(null);
      await load();
    } catch (e: any) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('rates.title')}</h1>
        <button onClick={openCreate} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition">
          {t('rates.create')}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={load} className="text-sm underline">{t('common.retry')}</button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">{editingId ? t('rates.edit') : t('rates.create')}</h2>
          {formError && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{formError}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rates.roomType')}</label>
                <select value={form.room_type_id} onChange={e => setForm(f => ({ ...f, room_type_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required>
                  <option value="">—</option>
                  {roomTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rates.pricePerNight')}</label>
                <input type="number" step="0.01" min="0.01" value={form.price_per_night}
                  onChange={e => setForm(f => ({ ...f, price_per_night: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de montant</label>
                <select value={form.amount_type} onChange={e => setForm(f => ({ ...f, amount_type: e.target.value as AmountType }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required>
                  {AMOUNT_TYPES.map(at => <option key={at.value} value={at.value}>{at.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rates.currency')}</label>
                <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value as Currency }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c} ({currencySymbol(c)})</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rates.startDate')}</label>
                <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rates.endDate')}</label>
                <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
                {saving ? t('common.loading') : t('common.save')}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : rates.length === 0 ? (
        <div className="text-center py-12 text-gray-500">{t('rates.noData')}</div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rates.roomType')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rates.pricePerNight')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rates.currency')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rates.startDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rates.endDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rooms.status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('rates.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rates.map(rate => (
                <tr key={rate.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{rate.room_type_name || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {currencySymbol(rate.currency)} {rate.price_per_night.toFixed(2)}
                    <span className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                      rate.amount_type === 'TTC' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>{rate.amount_type || 'HT'}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{rate.currency}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{rate.start_date}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{rate.end_date}</td>
                  <td className="px-6 py-4 text-sm">
                    {isExpired(rate.end_date) ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{t('rates.expired')}</span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{t('rates.active')}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    {isSuperAdmin ? (
                      <>
                        <button onClick={() => openEdit(rate)} className="text-purple-600 hover:text-purple-800">{t('rates.edit')}</button>
                        {deleteConfirm === rate.id ? (
                          <>
                            <button onClick={() => handleDelete(rate.id)} className="text-red-600 hover:text-red-800 font-medium">{t('common.confirm')}</button>
                            <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 hover:text-gray-700">{t('common.cancel')}</button>
                          </>
                        ) : (
                          <button onClick={() => setDeleteConfirm(rate.id)} className="text-red-500 hover:text-red-700">{t('rates.delete')}</button>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">{t('rates.readOnly')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
